/**
 * Scoped portal container for the Notice Editor.
 *
 * The source project owned the whole page, so its dialogs (`ModalDialog`) and its
 * tippy popovers (mention suggestions) were appended straight to `<body>`.
 *
 * Inside JustAct-CM the feature's stylesheet is deliberately confined to a
 * `.nte-root` subtree so it cannot reach the existing Material-UI screens. Anything
 * portalled to a bare `<body>` would therefore fall outside that scope and lose its
 * styling. This module hands out a single `<div class="nte-root nte-portal-root">`
 * that lives at the end of `<body>` - high enough in the stacking order to sit over
 * the Material-UI drawer/app-bar, and still inside the stylesheet's scope.
 */

const PORTAL_ID = "nte-portal-root";

/**
 * Returns the shared portal container, creating it on first use.
 * @returns {HTMLElement|null} null when there is no DOM (SSR / tests)
 */
export function getNoticeEditorPortalRoot() {
  if (typeof document === "undefined") return null;

  let node = document.getElementById(PORTAL_ID);
  if (!node) {
    node = document.createElement("div");
    node.id = PORTAL_ID;
    // `nte-root` keeps the feature's scoped CSS applying to portalled content.
    node.className = "nte-root nte-portal-root";
    document.body.appendChild(node);
  }
  return node;
}

/**
 * Removes the shared portal container if the feature left it empty.
 * Called when the Notice Editor page unmounts so navigating away inside
 * JustAct-CM does not leave stray nodes on <body>.
 */
export function releaseNoticeEditorPortalRoot() {
  if (typeof document === "undefined") return;
  const node = document.getElementById(PORTAL_ID);
  if (node && node.childNodes.length === 0 && node.parentNode) {
    node.parentNode.removeChild(node);
  }
}
